// Minimal shape of the SDK's `LDRecord` singleton, so this module needs no
// dependency on `@launchdarkly/session-replay`.
export type Recorder = {
  getRecordingState: () => 'Recording' | 'NotRecording';
  start: (options?: { silent?: boolean }) => unknown;
};

/** Start recording if it is not already running. Never throws; errors go to `onError`. */
export function ensureRecordingActive(recorder: Recorder, onError?: (error: unknown) => void): void {
  try {
    if (recorder.getRecordingState() !== 'Recording') {
      void recorder.start({ silent: true });
    }
  } catch (error) {
    onError?.(error);
  }
}

export type ReassertOptions = {
  // Checked on every event; recording is (re)started only while this returns true.
  isEnabled: () => boolean;
  onError?: (error: unknown) => void;
  // Minimum gap between activity-driven re-assert checks.
  activityThrottleMs?: number;
};

const DEFAULT_ACTIVITY_THROTTLE_MS = 30_000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll'] as const;
const ACTIVITY_LISTENER_OPTIONS = { passive: true, capture: true } as const;

/**
 * Re-assert recording while `isEnabled()`, on return-to-tab (focus /
 * visibility) and on throttled user activity — the latter covers a session that
 * lapses mid-use, where no foreground event fires. Returns a cleanup that
 * removes the listeners.
 */
export function subscribeReassertRecording(
  recorder: Recorder,
  { isEnabled, onError, activityThrottleMs = DEFAULT_ACTIVITY_THROTTLE_MS }: ReassertOptions,
): () => void {
  const reassert = () => {
    let enabled = false;
    try {
      enabled = isEnabled();
    } catch (error) {
      onError?.(error);
      return;
    }
    if (enabled) {
      ensureRecordingActive(recorder, onError);
    }
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      reassert();
    }
  };
  const onFocus = () => reassert();

  let lastActivityRun = 0;
  const onActivity = () => {
    const now = Date.now();
    if (now - lastActivityRun < activityThrottleMs) {
      return;
    }
    lastActivityRun = now;
    reassert();
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', onFocus);
  ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, onActivity, ACTIVITY_LISTENER_OPTIONS));

  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', onFocus);
    ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, onActivity, ACTIVITY_LISTENER_OPTIONS));
  };
}
